
import EventEmitter from 'events';
import Video, { AudioTrack, Participant, RemoteParticipant, RemoteTrack, RemoteTrackPublication, Room as TwilioRoom, TwilioError, VideoTrack } from 'twilio-video';
import { Room } from '../models/Room';
import { JoinedOrCreated, NotifyUsersOnNewVoiceCall } from '../models/local/Types';
import { Fire } from './FireClient';
import { My } from './My';
import { ScreensharingHelper, SCREEN_TRACK_NAME } from './ScreensharingHelper';
import { itpHooks, ITP_TRACK_PREFIX } from './interpretation/hooks';
import { KeepDeviceAwake } from './Utils/KeepDeviceAwake';
import { SoundUtil } from './Utils/SoundUtil';
import { UtilApp } from './Utils/UtilApp';


export class TwilioHelpEvent {
  constructor(public type: 'StateChange' | 'UserMuteChanged' | 'DominantSpeakerChanged' | 'NetworkQuality' | 'ScreenShareChanged',
    public userId?: string, public value?: boolean, public error?: string) {
  }
}

export declare interface TwilioHelper {
  on(event: 'Update', listener: (e: TwilioHelpEvent) => void): this;
}

export class TwilioHelper extends EventEmitter {
  participantSpans = new Map<string, HTMLSpanElement>(); // string is userId (identity)
  remoteParticipants = new Map<string, RemoteParticipant>(); // string is userId (identity)
  twilioRoom: TwilioRoom | undefined;
  isConnecting = false;
  /** 토큰 직접 연결 통화(통역 링크 등) — Firestore roomcalls 메타를 만들지/지우지 않음 */
  isTokenCall = false;
  audioInputLabel?: string;
  screenSharingHelper = new ScreensharingHelper();
  // 원격 화면공유 전용 렌더 대상. 참가자 아바타 타일과 분리해 큰 뷰어로 표시한다.
  screenShareSpan: HTMLSpanElement | null = null;
  screenSharerId: string | null = null; // 현재 화면을 공유 중인 원격 사용자 id
  subscribeToTwilioUpdates(handler: (e: TwilioHelpEvent | null) => void): () => void {
    twil.on('Update', handler);
    return () => twil.removeListener('Update', handler)
  }

  async addLocalVideo(html: HTMLDivElement) {
    const track = await Video.createLocalVideoTrack()
    html.appendChild(track.attach());
  }

  addParticipantSpan(userId: string): HTMLSpanElement {
    const partEl = document.createElement('span');
    partEl.setAttribute('id', userId);
    this.participantSpans.set(userId, partEl);
    return partEl;
  }

  async join(room: Room, identity: string): Promise<JoinedOrCreated> {
    let res = await this.connect(room, identity, null);
    this.removeDisconnectedUsersFromRoomCall();
    return res;
  }

  async connect(room: Room, identity: string, notify: NotifyUsersOnNewVoiceCall): Promise<JoinedOrCreated> {
    if (this.twilioRoom?.state == 'connected') {
      this.leaveRoom();
    }
    this.isConnecting = true;
    this.emitUpdate(null);

    let token = await Fire.call.getRoomAndToken(identity, room.id);
    await this.attachAndWire(token, identity);
    this.isTokenCall = false;
    return Fire.call.createOrJoinRoomCall(room, My.user, notify);
  }

  /**
   * 통역 링크 통화 등 Firestore room 문서가 없는 토큰 직접 연결 (설계 §10.1 호스트 인앱).
   * roomcalls 메타데이터를 만들지 않으며, leaveRoom 도 이를 건너뛴다(isTokenCall).
   */
  async connectWithToken(token: string, identity: string): Promise<void> {
    if (this.twilioRoom?.state == 'connected') {
      this.leaveRoom();
    }
    this.isConnecting = true;
    this.emitUpdate(null);
    await this.attachAndWire(token, identity);
    this.isTokenCall = true;
  }

  /** 토큰으로 Twilio room 연결 + 이벤트 배선 (connect/connectWithToken 공용 — 기존 connect 본문 추출) */
  private async attachAndWire(token: string, identity: string): Promise<void> {
    let enableVideoForIPadAppWorkaround = UtilApp.isIPadApp();
    
    this.twilioRoom = await Video.connect(token, {
      video: enableVideoForIPadAppWorkaround,
      audio: true,
      dominantSpeaker: true,

      // 음질 최적화: Opus 코덱 + DTX(침묵 구간 전송 최소화)
      preferredAudioCodecs: [{ codec: 'opus', dtx: true }],
      // 음성 통화에 적합한 비트레이트 (24kbps — 동시 대화 품질 확보)
      maxAudioBitrate: 24000,

      // 대역폭 프로파일: Group Room에서 오디오 품질 보호
      bandwidthProfile: {
        video: {
          mode: 'collaboration',
          trackSwitchOffMode: 'detected',
          dominantSpeakerPriority: 'high',
          maxSubscriptionBitrate: 2400000,
        }
      },

      // 네트워크 품질 모니터링 활성화
      networkQuality: { local: 1, remote: 1 },

      // 글로벌 최적 경로 자동 선택
      region: 'gll',

      // 화면공유 시 VP8 시뮬캐스트 활용
      preferredVideoCodecs: [{ codec: 'VP8', simulcast: true }],
    });
    if (enableVideoForIPadAppWorkaround) {
      this.twilioRoom.localParticipant.videoTracks.forEach(vtrack => {
        vtrack.unpublish();
        vtrack.track.stop();
      })
    }
    this.updateDefaultDevices();

    // console.log('this.twilioRoom.localParticipant.audioTracks; is', this.twilioRoom.localParticipant.audioTracks);

    this.participantSpans.clear();
    this.remoteParticipants.clear();
    this.clearRemoteScreenShare();
    this.addParticipantSpan(identity);

    let partConnectedHandler = (participant: RemoteParticipant) => {
      const participantSpan = this.addParticipantSpan(participant.identity);
      this.remoteParticipants.set(participant.identity, participant);

      const trackSubscribed = (span: HTMLElement, track: RemoteTrack) => {
        if (track.kind == 'audio') {
          const audioEl = (track as AudioTrack).attach() as HTMLAudioElement;
          span.appendChild(audioEl);
          // 통역 레이어 재생 정책 훅 (itp-* = 메인 / 원음 = ducking). 훅 미등록 시 no-op
          itpHooks.onRemoteAudio?.(track, audioEl, participant.identity);
          this.emitUpdate(null);
        } else if (track.kind == 'data') {
          // DataTrack 은 attach 불가 — 아래 else 의 VideoTrack.attach() 캐스팅에 흘리면
          // 런타임 크래시. 통역 자막(itp-cc) 수신은 훅으로 위임, 그 외 데이터 트랙은 무시
          itpHooks.onRemoteData?.(track, participant.identity);
        } else if (track.kind == 'video' && track.name === SCREEN_TRACK_NAME) {
          // 화면공유 트랙: 작은 참가자 타일이 아니라 전용 뷰어로 라우팅
          this.attachRemoteScreenShare(participant.identity, track as VideoTrack);
          this.emitUpdate(new TwilioHelpEvent('ScreenShareChanged', participant.identity, true));
        } else {
          span.appendChild((track as VideoTrack).attach());
          this.emitUpdate(null);
        }
        console.log('Track attached', track.kind, track.name);
      };

      const trackUnsubscribed = (track: RemoteTrack) => {
        if (track.kind == 'audio') itpHooks.onRemoteAudioGone?.(track); // 통역 정책 상태 정리
        if (track.kind == 'video' && track.name === SCREEN_TRACK_NAME) {
          this.clearRemoteScreenShare();
          this.emitUpdate(new TwilioHelpEvent('ScreenShareChanged', participant.identity, false));
        } else if (track.kind != 'data') {
          (track as AudioTrack | VideoTrack).detach().forEach(element => {
            element.remove()
          });
          this.emitUpdate(null);
        }
      };

      participant.tracks.forEach((publication: RemoteTrackPublication) => {
        if (publication.isSubscribed && publication.track) {
          trackSubscribed(participantSpan, publication.track);
        }
      });

      participant.on('trackSubscribed', track => trackSubscribed(participantSpan, track));
      participant.on('trackUnsubscribed', trackUnsubscribed);
      participant.on('trackEnabled', () => this.emitUpdate(new TwilioHelpEvent('UserMuteChanged', participant.identity, false)));
      participant.on('trackDisabled', () => this.emitUpdate(new TwilioHelpEvent('UserMuteChanged', participant.identity, true)));

      this.emitUpdate(null);
      SoundUtil.playJoined();
    }

    let partDisconnectedHandler = (participant: Participant) => {
      this.participantSpans.delete(participant.identity); // = this.participantSpans.filter(e => e.id != participant.identity);
      this.remoteParticipants.delete(participant.identity);

      // 화면을 공유하던 참가자가 나가면 전용 뷰어도 정리
      if (this.screenSharerId === participant.identity) {
        this.clearRemoteScreenShare();
        this.emitUpdate(new TwilioHelpEvent('ScreenShareChanged', participant.identity, false));
      }

      this.emitUpdate(null);
      SoundUtil.playLeft();
    }

    let disconnected = (room: TwilioRoom, error: TwilioError) => {
      if (error) {
        console.warn("[Twilio] Disconnected with error:", error.code, error.message);
        this.twilioRoom = undefined;
        this.emitUpdate(new TwilioHelpEvent('StateChange', undefined, undefined, 'disconnected'));
      }
      this.stopScreenSharing();
      this.clearRemoteScreenShare();
      KeepDeviceAwake.allowSleep();
    }

    this.twilioRoom.participants.forEach(partConnectedHandler);
    this.twilioRoom.on('participantConnected', partConnectedHandler);
    this.twilioRoom.on('participantDisconnected', partDisconnectedHandler);
    this.twilioRoom.on('disconnected', disconnected);
    this.twilioRoom.on('dominantSpeakerChanged', (speaker: RemoteParticipant) => this.emitUpdate(new TwilioHelpEvent('DominantSpeakerChanged', speaker?.identity)))

    // 네트워크 재연결 이벤트 처리 (음성 끊김 시 사용자 피드백)
    this.twilioRoom.on('reconnecting', (error) => {
      console.warn('[Twilio] Reconnecting due to:', error?.message);
      this.emitUpdate(new TwilioHelpEvent('StateChange', undefined, undefined, 'reconnecting'));
    });
    this.twilioRoom.on('reconnected', () => {
      console.log('[Twilio] Reconnected successfully');
      this.emitUpdate(new TwilioHelpEvent('StateChange', undefined, undefined, 'reconnected'));
    });

    // 로컬 네트워크 품질 모니터링
    this.twilioRoom.localParticipant.on('networkQualityLevelChanged', (level: number) => {
      if (level <= 1) {
        console.warn(`[Twilio] Poor network quality: ${level}/5`);
      }
      this.emitUpdate(new TwilioHelpEvent('NetworkQuality', undefined, undefined, String(level)));
    });

    this.isConnecting = false;
    this.emitUpdate(null);
    KeepDeviceAwake.keepAwake();
  }

  async leaveRoom() {

    // 연결을 끊기 전에 녹음을 먼저 마감한다 — disconnect 가 마이크 트랙을 죽이면
    // MediaRecorder 가 빈 blob 을 내 회의록 녹음이 유실된다 (미사용 시 no-op)
    await itpHooks.onBeforeDisconnect?.().catch(() => { /* 마감 실패해도 종료는 진행 */ });
    itpHooks.onCallLeft?.(); // 통역 세션·발행 트랙 정리 (미사용 시 no-op)
    if (this.getRoomId()) {
      // 토큰 직접 연결 통화는 roomcalls 메타가 없으므로 Firestore 정리 생략
      if (!this.isTokenCall) Fire.call.leaveRoomCall(this.getRoomId(), My.user.id);
      this.twilioRoom?.disconnect();
      this.twilioRoom = undefined;
      this.audioInputLabel = undefined;
    }
    this.isTokenCall = false;
    this.stopScreenSharing();
    this.clearRemoteScreenShare();
    this.emitUpdate(null);
  }

  isConnected() {
    return this.twilioRoom?.state == 'connected';
  }

  getRoomId() {
    return this.twilioRoom?.name ?? "";
  }

  async removeDisconnectedUsersFromRoomCall() {
    if (this.participantSpans.size == 1) {
      // Alone in the call.
      const roomCall = await Fire.call.getRoomCall(this.getRoomId());
      if (roomCall?.users.filter(u => u.id != My.user.id).length > 0) {
        console.log('Removing disconnected users from room call');
        roomCall.userIds = [My.user.id];
        roomCall.users = [My.user];
        Fire.call.updateRoomCall(roomCall);
      }
    }
  }

  enableLocalMicrophone(value: boolean) {
    // 통역 트랙(itp-*)이 함께 발행되면 [0]이 마이크라는 보장이 없다 —
    // itp 접두사를 제외한 모든 오디오(=마이크)를 제어 (설계 §4.0 함정 수정)
    let tracks = this.twilioRoom?.localParticipant.audioTracks;
    const micPubs = Array.from(tracks?.values() ?? [])
      .filter(pub => !pub.trackName.startsWith(ITP_TRACK_PREFIX));
    if (micPubs.length > 0) {
      micPubs.forEach(pub => pub.track.enable(value));
      itpHooks.onLocalMute?.(value); // 통역 입력(clone)도 동기 제어 — 미등록 시 no-op
      this.emitUpdate(null);
    } else {
      console.error("TwilioHelper.enableLocalMicrophone: Could not find a local audio track.")
    }
  }

  isMicEnabled(): boolean {
    let tracks = this.twilioRoom?.localParticipant.audioTracks;
    const micPub = Array.from(tracks?.values() ?? [])
      .find(pub => !pub.trackName.startsWith(ITP_TRACK_PREFIX));
    return micPub?.track.isEnabled ?? false;
  }

  emitUpdate(e: TwilioHelpEvent | null) {
    this.emit('Update', e ?? new TwilioHelpEvent('StateChange'));
  }

  updateDefaultDevices() {
    if (navigator.mediaDevices) {
      navigator.mediaDevices.enumerateDevices().then(devices => {
        this.audioInputLabel = devices.find(device => device.kind === 'audioinput' && device.deviceId === 'default')?.label;
      });
    }
  }

  startScreenSharing(onStop: () => void) {
    if (this.twilioRoom) {
      return this.screenSharingHelper.startSharing(this.twilioRoom, onStop)
    }
  }

  stopScreenSharing() {
    this.screenSharingHelper.stopSharing(this.twilioRoom);
  }

  isScreenSharing() {
    return this.screenSharingHelper.isSharing();
  }

  /** 현재 화면을 공유 중인 원격 사용자 id (없으면 null) */
  getScreenSharerId(): string | null {
    return this.screenSharerId;
  }

  /** 원격 화면공유 비디오 트랙을 전용 span에 attach (참가자 타일과 분리) */
  private attachRemoteScreenShare(identity: string, track: VideoTrack) {
    if (!this.screenShareSpan) {
      this.screenShareSpan = document.createElement('span');
      this.screenShareSpan.setAttribute('id', 'screen-share');
    }
    // 이전 화면공유 비디오가 남아있으면 비우고 새 트랙으로 교체
    while (this.screenShareSpan.firstChild) {
      this.screenShareSpan.removeChild(this.screenShareSpan.firstChild);
    }
    this.screenShareSpan.appendChild(track.attach());
    this.screenSharerId = identity;
  }

  /** 원격 화면공유 종료 시 전용 span 비우고 상태 초기화 */
  private clearRemoteScreenShare() {
    this.screenSharerId = null;
    if (this.screenShareSpan) {
      while (this.screenShareSpan.firstChild) {
        this.screenShareSpan.removeChild(this.screenShareSpan.firstChild);
      }
    }
  }
}

export var twil = new TwilioHelper();
